import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CollaborationDetailsComponent } from '../collaboration-details/collaboration-details.component';
import { CollaborationDataService } from '../../services/collaboration-data.service';
import { CollaborationDatasetComponent } from '../collaboration-dataset/collaboration-dataset.component';
import { CollaborationInvitationComponent } from '../collaboration-invitation/collaboration-invitation.component';
import { CollaborationSummaryComponent } from '../collaboration-summary/collaboration-summary.component';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { CreateCollaboration, UpdateCollaboration, SendCollaborationInvite } from '../../../../graphql/mutations';
import { GetCollaboration } from '../../../../graphql/queries';
import {
  collaborationCreateInput,
  collaborationUpdateInput,
  Action,
  CollaborationStatus,
  CreateCollaborationMutation,
  UpdateCollaborationMutation,
  SendCollaborationInviteMutation,
  SendPartnerInviteRequest,
  GetCollaborationQuery,
  CollaborationDetailsRequest,
  OrgType,
} from '../../../../API';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CollaborationItem } from '../../models/collaboration.model';

@Component({
  selector: 'app-create-collaboration',
  templateUrl: './create-collaboration.component.html',
  styleUrl: './create-collaboration.component.scss',
})
export class CreateCollaborationComponent implements OnInit, OnDestroy {
  @ViewChild('collaborationDetailsComponent') collaborationDetailsComponent!: CollaborationDetailsComponent;
  @ViewChild('collaborationDatasetComponent') collaborationDatasetComponent!: CollaborationDatasetComponent;
  @ViewChild('collaborationInvitationComponent') collaborationInvitationComponent!: CollaborationInvitationComponent;
  @ViewChild('collaborationSummaryComponent') collaborationSummaryComponent!: CollaborationSummaryComponent;

  currentStep = 0;
  totalSteps = 4;
  collaborationData: any | null = null;
  isLoading = false;
  collaborationId: string | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public collaborationDataService: CollaborationDataService,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.collaborationId = params['collaborationId'] || null;
      this.checkForExistingCollaboration();
    });
    this.currentStep = this.collaborationDataService.currentStep;
  }

  ngOnDestroy(): void {
    this.collaborationDataService.clearData();
  }

  private checkForExistingCollaboration(): void {
    if (this.collaborationId) {
      this.loadCollaborationData(this.collaborationId);
    } else {
      this.collaborationDataService.clearData();
    }
  }

  private loadCollaborationData(collaborationId: string): void {
    this.isLoading = true;
    const orgType = this.userManagementService.getOrgType();
    const loggedInUserOrg = this.userManagementService.getOrganizationType();
    if (!orgType) {
      this.nzMessageService.error('Unable to get organization information. Please refresh and try again.');
      this.isLoading = false;
      return;
    }

    const collaborationRequest: CollaborationDetailsRequest = {
      action: Action.GET_COLLABORATION,
      collaborationId: collaborationId,
      orgType: orgType,
      loggedInUserOrg: loggedInUserOrg as OrgType,
    };

    this.apiService
      .query<GetCollaborationQuery>(GetCollaboration, {
        collaborationRequest,
      })
      .then((response) => {
        const collaboration = response.data?.GetCollaboration?.data;

        if (collaboration) {
          this.collaborationDataService.originalCollaborationData = { ...collaboration } as CollaborationItem;
          this.collaborationDataService.updateCollaborationDetails(collaboration as CollaborationItem);
          this.collaborationDataService.selectedDatasets = collaboration.ownerDatasets || [];
        } else {
          throw new Error(response.data?.GetCollaboration?.message || 'Failed to load collaboration');
        }
      })
      .catch((error: any) => {
        console.error('Error loading collaboration:', error);
        this.nzMessageService.error(error?.message ?? 'Failed to load collaboration. Please try again.');
        this.router.navigateByUrl('/collaboration/list');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
  onCollaborationDetailsNext(): void {
    this.currentStep = this.collaborationDataService.currentStep;
  }

  async onCollaborationDetailsSaveAsDraft(): Promise<void> {
    await this.saveCollaborationAsDraft();
  }

  onCollaborationDetailsCancel(): void {
    this.collaborationDataService.clearData();
    this.router.navigateByUrl('/collaboration/list');
  }

  onDatasetNext(): void {
    this.currentStep = this.collaborationDataService.currentStep;
  }

  onDatasetBack(): void {
    this.currentStep = this.collaborationDataService.currentStep;
  }

  onDatasetCancel(): void {
    this.collaborationDataService.clearData();
    this.router.navigateByUrl('/collaboration/list');
  }

  onInvitationNext(): void {
    this.currentStep = this.collaborationDataService.currentStep;
  }

  onInvitationCancel(): void {
    this.collaborationDataService.clearData();
    this.router.navigateByUrl('/collaboration/list');
  }

  onCancel(): void {
    if (this.currentStep > 0) {
      this.resetCurrentStep();
      this.collaborationDataService.currentStep = this.currentStep - 1;
      this.currentStep = this.collaborationDataService.currentStep;
    } else {
      this.collaborationDataService.clearData();
      this.router.navigateByUrl('/collaboration/list');
    }
  }

  async onSaveAsDraft(): Promise<void> {
    await this.syncCurrentStepDataToService();
    await this.saveCollaborationAsDraft();
  }

  private async syncCurrentStepDataToService(): Promise<void> {
    switch (this.currentStep) {
      case 0:
        if (this.collaborationDetailsComponent) {
          this.collaborationDetailsComponent.onSaveAsDraft();
        }
        break;
      case 1:
        if (this.collaborationDatasetComponent) {
          this.collaborationDatasetComponent.onSaveAsDraft();
        }
        break;
      case 2:
        if (this.collaborationInvitationComponent) {
          this.collaborationInvitationComponent.onSaveAsDraft();
        }
        break;
    }
  }

  private async saveCollaborationAsDraft(): Promise<void> {
    try {
      this.isLoading = true;
      const collaborationData = this.collaborationDataService.getCompleteCollaborationData();

      if (!collaborationData.name || !collaborationData.description || !collaborationData.objective) {
        this.nzMessageService.error('Please fill in all required fields before saving as draft.');
        return;
      }

      const currentUser = this.userManagementService.getEmail();
      const orgType = this.userManagementService.getOrgType();

      if (!currentUser || !orgType) {
        this.nzMessageService.error('Unable to get user information. Please refresh the page and try again.');
        return;
      }

      const isUpdate = !!collaborationData.collaborationId;

      if (isUpdate) {
        await this.updateCollaborationAsDraft(collaborationData, currentUser, orgType);
      } else {
        await this.createCollaborationAsDraft(collaborationData, currentUser, orgType);
      }
      this.collaborationDataService.errorMessage = '';
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to save collaboration as draft. Please try again.';
      this.collaborationDataService.errorMessage = errorMessage;
      console.error('Error saving collaboration as draft:', error);
      this.nzMessageService.error(error?.message ?? 'Failed to save collaboration as draft. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  private async createCollaborationAsDraft(
    collaborationData: any,
    currentUser: string,
    orgType: string,
  ): Promise<void> {
    const createInput: collaborationCreateInput = {
      action: Action.CREATE,
      createdBy: currentUser,
      name: collaborationData.name,
      description: collaborationData.description,
      objective: collaborationData.objective,
      orgType: orgType,
      status: CollaborationStatus.DRAFT,
      ownerDatasets:
        collaborationData.ownerDatasets?.map((dataset: any) => ({
          dataConnectionId: dataset.dataConnectionId,
          dataConnectionName: dataset.dataConnectionName,
          schemaName: dataset.schemaName,
          tableName: dataset.tableName,
        })) || [],
      partnerEmail: collaborationData.partnerEmail || [],
      partnerInviteMessage: collaborationData.partnerInviteMessage || '',
      partnerOrgType: collaborationData.partnerOrgType || '',
    };

    const response = await this.apiService.query<CreateCollaborationMutation>(CreateCollaboration, {
      collaborationRequest: createInput,
    });

    if (response.data?.CreateCollaboration?.success) {
      const createdCollaboration = response.data.CreateCollaboration.data;
      if (createdCollaboration) {
        this.collaborationDataService.updateCollaborationDetails({
          collaborationId: createdCollaboration.collaborationId,
        });
      }
      this.nzMessageService.success(
        response.data?.CreateCollaboration?.message || 'Collaboration saved as draft successfully!',
      );
    } else {
      throw new Error(response.data?.CreateCollaboration?.message || 'Failed to create collaboration');
    }
  }

  private async updateCollaborationAsDraft(
    collaborationData: any,
    currentUser: string,
    orgType: string,
  ): Promise<void> {
    const updateInput: collaborationUpdateInput = {
      action: Action.UPDATE,
      collaborationId: collaborationData.collaborationId || collaborationData.id,
      lastModifiedBy: currentUser,
      name: collaborationData.name,
      description: collaborationData.description,
      objective: collaborationData.objective,
      orgType: orgType,
      status: CollaborationStatus.DRAFT,
      ownerDatasets:
        collaborationData.ownerDatasets?.map((dataset: any) => ({
          dataConnectionId: dataset.dataConnectionId,
          dataConnectionName: dataset.dataConnectionName,
          schemaName: dataset.schemaName,
          tableName: dataset.tableName,
        })) || [],
      partnerEmail: collaborationData.partnerEmail || [],
      partnerInviteMessage: collaborationData.partnerInviteMessage || '',
      partnerOrgType: collaborationData.partnerOrgType || '',
    };

    const response = await this.apiService.query<UpdateCollaborationMutation>(UpdateCollaboration, {
      collaborationRequest: updateInput,
    });

    if (response.data?.UpdateCollaboration?.success) {
      console.log(response.data?.UpdateCollaboration?.message);
    } else {
      throw new Error(response.data?.UpdateCollaboration?.message || 'Failed to update collaboration');
    }
  }

  async onNext(): Promise<void> {
    await this.saveAndProceedToNext();
  }

  private async saveAndProceedToNext(): Promise<void> {
    if (!this.isCurrentStepValid()) {
      return;
    }
    if (this.currentStep == 3) return this.onCompleteCollaboration();

    await this.syncCurrentStepDataToService();
    await this.saveCollaborationAsDraft();

    if (!this.isLoading && !this.collaborationDataService.errorMessage) {
      this.proceedToNextStep();
    }
  }

  private isCurrentStepValid(): boolean {
    if (this.collaborationDataService.errorMessage) return false;
    switch (this.currentStep) {
      case 0:
        if (!this.collaborationDetailsComponent?.collaborationForm.valid) {
          this.nzMessageService.error('Please fill in all required fields before proceeding.');
          return false;
        }
        break;
      case 1:
        if (!this.collaborationDatasetComponent?.isValid()) {
          this.nzMessageService.warning('Please select at least one dataset to continue');
          return false;
        }
        break;
      case 2:
        if (!this.collaborationInvitationComponent?.isValid()) {
          this.nzMessageService.warning('Please validate partner email to continue');
          return false;
        }
        break;
    }
    return true;
  }

  private proceedToNextStep(): void {
    switch (this.currentStep) {
      case 0:
        this.collaborationDetailsComponent?.onNext();
        break;
      case 1:
        this.collaborationDatasetComponent?.onNext();
        break;
      case 2:
        this.collaborationInvitationComponent?.onNext();
        break;
      case 3:
        this.onCompleteCollaboration();
        break;
    }
  }

  private resetCurrentStep(): void {
    if (!this.collaborationDataService.errorMessage) return;

    switch (this.currentStep) {
      case 0:
        this.collaborationDetailsComponent?.reset();
        break;
      case 1:
        this.collaborationDatasetComponent?.reset();
        break;
      case 2:
        this.collaborationInvitationComponent?.reset();
        break;
    }
  }

  onBack(): void {
    if (this.currentStep > 0) {
      this.resetCurrentStep();
      this.collaborationDataService.currentStep = this.currentStep - 1;
      this.currentStep = this.collaborationDataService.currentStep;
    }
  }

  isValidAction(): boolean {
    switch (this.currentStep) {
      case 0:
        return this.collaborationDataService.isStep1Valid;
      case 1:
        return this.collaborationDataService.isStep2Valid;
      case 2:
        return this.collaborationDataService.isStep3Valid;
      case 3:
        return true; // Summary step is always valid
      default:
        return false;
    }
  }

  canNext(): boolean {
    return this.isValidAction() && !this.isLoading && !this.collaborationDataService.errorMessage;
  }

  onCompleteCollaboration(): void {
    const collaborationData = this.collaborationDataService.getCompleteCollaborationData();
    const orgType = this.userManagementService.getOrgType();

    if (!collaborationData.collaborationId) {
      this.nzMessageService.error('Collaboration ID not found. Please save as draft first.');
      return;
    }

    if (!orgType) {
      this.nzMessageService.error('Unable to get organization information. Please refresh and try again.');
      return;
    }

    const sendInviteInput: SendPartnerInviteRequest = {
      action: Action.SEND_INVITE,
      collaborationId: collaborationData.collaborationId,
      orgType: orgType,
      lastModifiedBy: this.userManagementService.getEmail(),
    };

    this.isLoading = true;

    this.apiService
      .query<SendCollaborationInviteMutation>(SendCollaborationInvite, {
        collaborationRequest: sendInviteInput,
      })
      .then((response) => {
        const sendCollaborationInvite = response.data?.SendCollaborationInvite;

        if (sendCollaborationInvite?.success) {
          this.nzMessageService.success(sendCollaborationInvite.message || 'Collaboration invites sent successfully!');
          this.collaborationDataService.clearData();
          this.router.navigateByUrl('/collaboration/list');
        } else {
          throw new Error(sendCollaborationInvite?.message || 'Failed to send collaboration invites');
        }
      })
      .catch((error: any) => {
        console.error('Error sending collaboration invites:', error);
        this.nzMessageService.error(error?.message ?? 'Failed to send collaboration invites. Please try again.');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  onSummaryEditSection(section: string): void {
    switch (section) {
      case 'createCollaboration':
        this.collaborationDataService.currentStep = 0;
        this.currentStep = 0;
        break;
      case 'dataset':
        this.collaborationDataService.currentStep = 1;
        this.currentStep = 1;
        break;
      case 'partners':
        this.collaborationDataService.currentStep = 2;
        this.currentStep = 2;
        break;
    }
  }
}
